import React, { ReactNode, ReactElement, ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactElement;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="flex h-screen items-center justify-center bg-primary">
            <div className="card max-w-md p-8">
              <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(239,68,68,0.1)]">
                <svg className="h-6 w-6 text-[var(--status-danger)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="mb-2 text-lg font-black text-primary">Something went wrong</h2>
              <p className="mb-4 text-sm text-secondary">{this.state.error?.message || 'An unexpected error occurred'}</p>
              {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                <details className="mb-4 text-xs text-tertiary">
                  <summary className="cursor-pointer font-bold">Error Details</summary>
                  <pre className="mt-2 overflow-auto rounded bg-tertiary p-2">{this.state.errorInfo.componentStack}</pre>
                </details>
              )}
              <button
                onClick={this.handleReset}
                className="btn-brand w-full"
              >
                Try Again
              </button>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
